/**
 * useWindRaster.ts
 * يحمّل نسيج الرياح العالمي (GFS GRIB2 → PNG: R=السرعة، G=U، B=V) ويفكّه إلى شبكة رياح
 * (WeatherGrid مع u/v بـ km/h) يستهلكها نظام الجسيمات وطبقة لون الرياح — مجاناً وبلا Open-Meteo.
 *
 * يُصغَّر إلى شبكة 1° (360×181) كافية لحقل تدفّق سلس؛ يتغيّر مع الشريط الزمني (لكل ساعة نسيج).
 *
 * الأداء: الشبكة تمثيل مسطّح (Float32Array عبر WeatherGrid.flat) لا مصفوفة 65 ألف كائن —
 * الفكّ على canvas مفرد مُعاد الاستخدام، والمزج الزمني حلقة رقمية بلا أي إنشاء كائنات،
 * مع كاش صغير للإطارات المفكوكة كي لا يتكرّر الفكّ عند الترجيع/إعادة التشغيل.
 */

import { useEffect, useState } from 'react';
import type { WeatherGrid, FlatGridData } from '../../../services/weatherGridService';
import { rasterUrl, whenRasterVersionKnown } from '../../../services/rasterVersion';
import { loadRasterImage, type RasterImage } from '../../../services/rasterImageCache';
import { queuePrefetch } from '../../../services/prefetchQueue';

const UV_MAX = 60;          // يطابق WIND_UV_MAX في weatherTextures.ts / السكربت
const WIND_SPEED_MAX = 120; // يطابق VALUE_RANGES['wind'] و WIND_SPEED_MAX في السكربت
// تصغير ÷2 من نسيج المصدر (1440×721 بدقّة 0.25°) → 0.5° بدل 0.75°.
// كان ÷3، وهو يمسح بنية مركز الإعصار: عين الإعصار ~30-50 كم بينما خلية 0.75° تساوي 83 كم،
// فيصير الدوران قرب المركز حقلاً ممسوحاً لا لولباً محكماً كما في Zoom Earth. الكلفة صارت
// محتملة بعد نقل المزج الزمني إلى الـ GPU (لم يعد هناك lerp على الـ CPU لكل خطوة زمنية).
const GRID_W = 720;
const GRID_H = 361;
const N = GRID_W * GRID_H;

// canvas/context مفردان للفكّ (إنشاؤهما لكل إطار كان يترك نفايات GC)
let decodeCanvas: HTMLCanvasElement | null = null;
let decodeCtx: CanvasRenderingContext2D | null = null;
function getDecodeCtx(): CanvasRenderingContext2D | null {
    if (!decodeCtx) {
        decodeCanvas = document.createElement('canvas');
        decodeCanvas.width = GRID_W;
        decodeCanvas.height = GRID_H;
        decodeCtx = decodeCanvas.getContext('2d', { willReadFrequently: true });
    }
    return decodeCtx;
}

// كاش الإطارات المفكوكة (مفتاحه dir+index) — يغطي نافذة التشغيل والترجيع
const FLAT_CACHE_MAX = 8;   // 8 × 3 × 260k × 4 بايت ≈ 25 ميغابايت
const flatCache = new Map<string, FlatGridData>();

function makeGrid(flat: FlatGridData): WeatherGrid {
    return {
        bounds: { north: 90, south: -90, east: 180, west: -180 },
        rows: GRID_H, cols: GRID_W, points: [], flat,
        timestamp: '', type: 'wind', source: 'gfs-raster', unit: 'km/h',
    };
}

/*
 * ── بوّابة «الساعة المعروضة أوّلاً» ──────────────────────────────────────────────
 *
 * الجسيمات تقرأ ثلاث شبكات: السابقة والحالية والتالية (المزج الزمني على الـ GPU).
 * وكان الخطّافات الثلاثة تنطلق **معاً** بنفس الأولوية. فعند الضغط على نقطة في مسار
 * إعصار — قفزة إلى ساعة لم تُحمَّ بعد — تتنافس ثلاثة ملفّات (‏~345 ك.ب لكل منها) على
 * نفس النطاق، فتصل الساعةُ المعروضة في نحو ثلاثة أضعاف زمنها لو كانت وحدها. وهذا
 * بالضبط الفارق المرصود: اللون يتبدّل فوراً (نسيجه يُحمَّل مباشرةً وحده) والجسيمات
 * تتلكّأ خلفه.
 *
 * فصار الجاران يمرّان من هنا: ينتظران انتهاء الساعة المعروضة، ثم يمرّان عبر طابور
 * الإحماء المحدود التزامن (‏وضع التركيز يضيّقه حتى يصل ما ينتظره المستخدم). لا يُلغى
 * تنزيل ولا تنقص دقّة — يتغيّر الترتيب فقط. والحالة الشائعة لا تتأثّر أصلاً: أثناء
 * التشغيل تكون الساعة المعروضة في الكاش، فالبوّابة مفتوحة سلفاً وينطلق الجاران فوراً.
 */
let nowGate: Promise<unknown> = Promise.resolve();

/**
 * @param enabled بوّابة تحميل. قبل مزامنة الشريط الزمني على "الآن" يكون timeIndex صفراً،
 * فكان يُنزَّل نسيج الإطار 0 (وجارَيه) ثم يُرمى فوراً حين يقفز الشريط إلى الساعة الحالية —
 * **ثلاثة ملفّات كاملة مهدورة على المسار الحرج** لكل إقلاع. رُصدت في جرد الشبكة:
 * wind_000 و wind_001 تُطلَبان قبل wind_005/006/007. الطبقات اللونية كانت محروسة بـ
 * timelineReady أصلاً؛ هذا الخطّاف وحده كان بلا حارس.
 */
export function useWindRaster(
    timeIndex: number, dir = 'rasters/gfs/', enabled = true,
    /** `neighbour` = شبكة جوار للمزج: لا تنطلق قبل أن تصل الساعة المعروضة. */
    role: 'now' | 'neighbour' = 'now',
): WeatherGrid | null {
    const [grid, setGrid] = useState<WeatherGrid | null>(null);

    useEffect(() => {
        if (!enabled) return;
        const key = `${dir}${timeIndex}`;
        const cached = flatCache.get(key);
        if (cached) {
            // تحديث ترتيب LRU
            flatCache.delete(key); flatCache.set(key, cached);
            setGrid(makeGrid(cached));
            return;
        }
        let cancelled = false;
        // عبر الكاش المشترك لا Image خاصّ. جرد الشبكة أظهر أن الإطار الواحد يُطلَب
        // مرّتين أو ثلاثاً من مسارات مستقلّة (هذا الخطّاف، وقارئ التلميح، وطبقة اللون)،
        // وكل Image جديد يفكّ نفس الملفّ من جديد: **4 ميغابايت بكسلات لكل تكرار**.
        // loadRasterImage يوحّد الطلبات الطائرة ويحتفظ بالصورة المفكوكة، فيصير الفكّ
        // مرّة واحدة مهما تعدّد الطالبون — بلا أي أثر على الدقّة أو الشكل.
        const fetchImage = () => loadRasterImage(rasterUrl(dir, `wind_${String(timeIndex).padStart(3, '0')}.png`));
        const request = role === 'now'
            // الساعة المعروضة: طلب مباشر بلا طابور — هي ما يحدّق فيه المستخدم الآن.
            // وتصير هي البوّابة التي ينتظرها الجاران.
            ? whenRasterVersionKnown().then(() => { const p = fetchImage(); nowGate = p.catch(() => undefined); return p; })
            : whenRasterVersionKnown()
                .then(() => nowGate.catch(() => undefined))
                .then(() => new Promise<RasterImage>((resolve, reject) => {
                    if (cancelled) { reject(new Error('cancelled')); return; }
                    queuePrefetch(() => fetchImage().then(resolve, reject), 'urgent');
                }));
        void request
            .then((img) => {
                if (cancelled) return;
                const ctx = getDecodeCtx();
                if (!ctx) return;
                ctx.drawImage(img, 0, 0, GRID_W, GRID_H);   // تصغير تلقائي بمتوسّط
                const data = ctx.getImageData(0, 0, GRID_W, GRID_H).data;

                // صفّ الصورة 0 = الجنوب (السكربت قلب lat)، وهو ما يطابق الصفّ 0 = الجنوب هنا.
                const u = new Float32Array(N);
                const v = new Float32Array(N);
                const value = new Float32Array(N);
                for (let i = 0; i < N; i++) {
                    const p = i * 4;
                    const uu = ((data[p + 1] / 255) * 2 - 1) * UV_MAX;   // km/h
                    const vv = ((data[p + 2] / 255) * 2 - 1) * UV_MAX;
                    u[i] = uu; v[i] = vv;
                    // السرعة تُقرأ من القناة R (محسوبة مسبقاً على الخادم من الشبكة الأصلية
                    // الكاملة، قبل أي تصغير) بدل اشتقاقها من U/V بعد تصغير المتصفّح للصورة —
                    // متوسّط متجهات الرياح قبل حساب مقدارها (بدل متوسّط المقدار مباشرة) يُلغي
                    // مكوّنات متعاكسة في مناطق القص الريحي (fronts/تقارب)، فيُظهر سرعة أقل من
                    // الحقيقة وألواناً باهتة/خاطئة لا تطابق Zoom Earth عند نفس الإحداثي.
                    value[i] = (data[p] / 255) * WIND_SPEED_MAX;
                }
                const flat: FlatGridData = { value, u, v };
                flatCache.set(key, flat);
                if (flatCache.size > FLAT_CACHE_MAX) {
                    const oldest = flatCache.keys().next().value;
                    if (oldest !== undefined) flatCache.delete(oldest);
                }
                setGrid(makeGrid(flat));
            })
            .catch(() => { /* الساعة غير مولَّدة بعد — نُبقي آخر شبكة */ });
        return () => { cancelled = true; };
    }, [timeIndex, dir, enabled]);

    return grid;
}
